/**
 * Implementation of Dashboard Layout component
 *
 * @author - Harsh Tomar
 */

// Dependencies
import React, { ReactChild, useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { MdOutlineDomainVerification } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import { RiBillLine } from "react-icons/ri";
import HeaderAvatarMenu from "./HeaderAvatarMenu";
import TopBanner from "./TopBanner";
import PurchaseLTD from "./Billing/Purchase";
import { useGetCurrentUser } from "../api/reactQuery";
import { When } from "react-if";
import { PlanName, UserResponse } from "../api/responses";
import MonitorlyLogo from './../images/monitorly-logo.png';

type Props = {
  children: ReactChild;
  contentNoPadding?: boolean;
};

export default function DashboardLayout({ children, contentNoPadding }: Props) {
  const { pathname } = useLocation();
  const { data: currentUser } = useGetCurrentUser(true);

  const menuItems = [
    { text: "Domains", to: "/home", icon: MdOutlineDomainVerification },
    // { text: "Monitors", to: "/monitors", icon: BsCollection },
    { text: "Billing", to: "/billing", icon: RiBillLine },
    { text: "Settings", to: "/settings/general", icon: IoSettingsOutline },
  ];

  return (
    <div>
      <When condition={currentUser?.planName === PlanName.TRIAL_EXPIRED}>
        <div className="p-2 flex justify-center items-center bg-rose-600">
          <p className="text-center text-white">
            Your FREE Trial has been expired. Upgrade now to continue monitoring
            your domains.
          </p>
        </div>
      </When>
      <When condition={currentUser?.planName === PlanName.FREE_TRIAL}>
        <div className="p-2 flex justify-center items-center bg-amber-500">
          <p className="text-center text-white">
            Your free trial is expiring soon.
          </p>
        </div>
      </When>
      {/* <When
        condition={
          currentUser?.planName === PlanName.FREE_TRIAL ||
          currentUser?.planName === PlanName.TRIAL_EXPIRED
        }
      >
        <TopBanner
          message={
            <p>
              Limited time offer. Pay $78 once and monitor UNLIMITED domains
              lifetime.{" "}
              <PurchaseLTD
                cta={
                  <button className="btn-accent cursor-pointer hover:ring-offset-[#FFD200]">
                    Get Offer
                  </button>
                }
              />
            </p>
          }
        />
      </When> */}

      <div className="flex">
        {/* Dashboard Sidebar Start*/}
        <div className="w-80 h-[calc(100vh - 5rem)] bg-primary">
          <div style={{ width: "calc(100% - 2.5rem" }}>
            <div className="py-3 px-5">
              <img src={MonitorlyLogo} className="w-[70%]"/>
              
            </div>
            {/* Menu Container */}
            <div className="px-5 py-5">
              {menuItems.map((item, index) => (
                <Link to={`/dashboard${item.to}`} key={index}>
                  <li
                    className={`px-4 py-1.5 text-white list-none text-lg rounded-lg my-2 transition-all ${
                      pathname.startsWith(`/dashboard${item.to}`)
                        ? "bg-accent font-semibold"
                        : "hover:bg-white hover:bg-opacity-10 text-gray-200"
                    }`}
                  >
                    {item.icon({ className: "inline mr-4 -mt-1" })}
                    {item.text}
                  </li>
                </Link>
              ))}
            </div>
          </div>
        </div>
        {/* Dashboard Sidebar ends */}

        {/* Dashboard Content Starts */}
        <div className="flex-1 bg-gray-200 rounded-tl-3xl -ml-10 flex flex-col">
          {/* Dashboard Header Starts */}
          <div className="bg-white h-20 rounded-tl-3xl p-4 flex items-center justify-between shadow border-b">
            <h1 className="text-2xl font-semibold text-primary">
              {
                menuItems.find((item) =>
                  pathname.startsWith(`/dashboard${item.to}`)
                )?.text
              }
            </h1>

            <div className="mx-4 flex items-center">
              {/* <div className="w-12 h-12 rounded-full bg-gray-200 flex justify-center items-center">
                {shotcutName}
              </div> */}
              <HeaderAvatarMenu user={currentUser as UserResponse} />
            </div>
          </div>

          {/* Dashboard Header Ends */}
          <div
            className={`overflow-auto ${contentNoPadding ? "p-0" : "p-4"}`}
            style={{ height: "calc(100vh - 5rem)" }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
